import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Edit, RefreshCw, Eye, EyeOff, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDocumentosPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadFormVisible, setUploadFormVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [replaceModalVisible, setReplaceModalVisible] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    title: '',
    summary: '',
    category: '',
    slug: '',
    file: null,
    visible: true,
    image_url: '',
    video_url: ''
  });
  const [slugPreview, setSlugPreview] = useState('');
  const [customSlug, setCustomSlug] = useState(false);
  
  // Replace form state
  const [replaceFile, setReplaceFile] = useState(null);
  
  const navigate = useNavigate();
  const { token } = useAuth();
  const { isDarkMode } = useDarkMode();

  useEffect(() => {
    fetchDocuments();
  }, []);

  // Auto-generate slug preview as user types title
  useEffect(() => {
    if (!customSlug && uploadForm.title) {
      const generated = uploadForm.title
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      setSlugPreview(generated);
    } else if (customSlug) {
      setSlugPreview(uploadForm.slug);
    } else {
      setSlugPreview('');
    }
  }, [uploadForm.title, uploadForm.slug, customSlug]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      
      // Get token from localStorage
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        setError('No autenticado');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/documentos`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      // Sort alphabetically for Phase 1
      const sortedDocs = response.data.documents.sort((a, b) => 
        a.title.localeCompare(b.title)
      );
      
      setDocuments(sortedDocs);
      setError(null);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError('Error al cargar documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.file) {
      alert('Por favor selecciona un archivo');
      return;
    }

    try {
      const authToken = localStorage.getItem('authToken');
      
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title);
      formData.append('summary', uploadForm.summary);
      formData.append('category', uploadForm.category);
      formData.append('slug', customSlug ? uploadForm.slug : '');
      formData.append('visible', uploadForm.visible);
      formData.append('image_url', uploadForm.image_url);
      formData.append('video_url', uploadForm.video_url);

      await axios.post(
        `${BACKEND_URL}/api/admin/documentos/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      alert('Documento subido exitosamente');
      setUploadFormVisible(false);
      setUploadForm({
        title: '',
        summary: '',
        category: '',
        slug: '',
        file: null,
        visible: true,
        image_url: '',
        video_url: ''
      });
      setCustomSlug(false);
      fetchDocuments();
    } catch (err) {
      console.error('Error uploading document:', err);
      alert(err.response?.data?.detail || 'Error al subir el documento');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    try {
      const authToken = localStorage.getItem('authToken');
      
      const formData = new FormData();
      formData.append('title', selectedDoc.title);
      formData.append('summary', selectedDoc.summary);
      formData.append('slug', selectedDoc.slug);
      formData.append('category', selectedDoc.category);
      formData.append('visible', selectedDoc.visible);
      formData.append('image_url', selectedDoc.image_url || '');
      formData.append('video_url', selectedDoc.video_url || '');

      await axios.put(
        `${BACKEND_URL}/api/admin/documentos/${selectedDoc.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      alert('Documento actualizado exitosamente');
      setEditModalVisible(false);
      setSelectedDoc(null);
      fetchDocuments();
    } catch (err) {
      console.error('Error updating document:', err);
      alert(err.response?.data?.detail || 'Error al actualizar el documento');
    }
  };

  const handleReplaceSubmit = async (e) => {
    e.preventDefault();

    if (!replaceFile) {
      alert('Por favor selecciona un archivo');
      return;
    }

    try {
      const authToken = localStorage.getItem('authToken');
      
      const formData = new FormData();
      formData.append('file', replaceFile);

      await axios.put(
        `${BACKEND_URL}/api/admin/documentos/${selectedDoc.id}/replace`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      alert('Contenido reemplazado exitosamente');
      setReplaceModalVisible(false);
      setSelectedDoc(null);
      setReplaceFile(null);
      fetchDocuments();
    } catch (err) {
      console.error('Error replacing content:', err);
      alert(err.response?.data?.detail || 'Error al reemplazar el contenido');
    }
  };

  const toggleVisibility = async (doc) => {
    try {
      const authToken = localStorage.getItem('authToken');
      
      const formData = new FormData();
      formData.append('title', doc.title);
      formData.append('summary', doc.summary);
      formData.append('slug', doc.slug);
      formData.append('category', doc.category);
      formData.append('visible', !doc.visible);
      formData.append('image_url', doc.image_url || '');
      formData.append('video_url', doc.video_url || '');

      await axios.put(
        `${BACKEND_URL}/api/admin/documentos/${doc.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      fetchDocuments();
    } catch (err) {
      console.error('Error toggling visibility:', err);
      alert('Error al cambiar la visibilidad');
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'
    }`}>
      {/* Header */}
      <header className={`${
        isDarkMode ? 'bg-purple-800' : 'bg-purple-600'
      } text-white py-6 transition-colors duration-300`}>
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/documentos')}
                className={`p-2 rounded-full transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-purple-700 hover:bg-purple-600' 
                    : 'bg-purple-700 hover:bg-purple-800'
                }`}
              >
                <ArrowLeft size={24} />
              </button>
              <h1 className="text-2xl md:text-3xl font-bold">
                Administrar Documentos
              </h1>
            </div>
            
            <button
              onClick={() => setUploadFormVisible(true)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
                isDarkMode 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              <Upload size={20} />
              <span>Subir Documento</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {loading && (
          <div className="text-center py-12">
            <div className={`animate-spin rounded-full h-12 w-12 border-b-2 mx-auto ${
              isDarkMode ? 'border-purple-400' : 'border-purple-600'
            }`}></div>
            <p className="mt-4">Cargando documentos...</p>
          </div>
        )}

        {error && (
          <div className={`text-center py-12 ${
            isDarkMode ? 'text-red-400' : 'text-red-600'
          }`}>
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="max-w-6xl mx-auto">
            <div className={`mb-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Total: {documents.length} documentos (ordenados alfabéticamente)
            </div>

            <div className="space-y-4">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className={`rounded-lg p-6 ${
                    isDarkMode ? 'bg-gray-800' : 'bg-white shadow-md'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-xl font-bold">{doc.title}</h3>
                        {doc.visible ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            Visible
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                            Oculto
                          </span>
                        )}
                      </div>
                      <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {doc.summary}
                      </p>
                      <div className={`flex flex-wrap gap-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <span>Slug: <code className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} px-2 py-1 rounded`}>{doc.slug}</code></span>
                        <span>Categoría: <strong>{doc.category}</strong></span>
                        <span>Tipo: <strong className="uppercase">{doc.file_type || 'markdown'}</strong></span>
                        <span>Archivo: {doc.filename}</span>
                        <span>👁️ <strong>{doc.view_count || 0}</strong> {(doc.view_count || 0) === 1 ? 'vista' : 'vistas'}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => toggleVisibility(doc)}
                        className={`p-2 rounded-lg transition-all duration-300 ${
                          isDarkMode 
                            ? 'bg-gray-700 hover:bg-gray-600' 
                            : 'bg-gray-200 hover:bg-gray-300'
                        }`}
                        title={doc.visible ? 'Ocultar' : 'Mostrar'}
                      >
                        {doc.visible ? <Eye size={20} /> : <EyeOff size={20} />}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedDoc(doc);
                          setEditModalVisible(true);
                        }}
                        className={`p-2 rounded-lg transition-all duration-300 ${
                          isDarkMode 
                            ? 'bg-blue-700 hover:bg-blue-600' 
                            : 'bg-blue-600 hover:bg-blue-700'
                        } text-white`}
                        title="Editar metadatos"
                      >
                        <Edit size={20} />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedDoc(doc);
                          setReplaceModalVisible(true);
                        }}
                        className={`p-2 rounded-lg transition-all duration-300 ${
                          isDarkMode 
                            ? 'bg-orange-700 hover:bg-orange-600' 
                            : 'bg-orange-600 hover:bg-orange-700'
                        } text-white`}
                        title="Reemplazar contenido"
                      >
                        <RefreshCw size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {uploadFormVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Subir Nuevo Documento</h2>
              <button
                onClick={() => setUploadFormVisible(false)}
                className={`p-2 rounded-lg transition-all duration-300 ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                }`}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleUploadSubmit} className="space-y-4">
              <div>
                <label className="block mb-2 font-semibold">Título *</label>
                <input
                  type="text"
                  required
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold">Resumen</label>
                <textarea
                  value={uploadForm.summary}
                  onChange={(e) => setUploadForm({...uploadForm, summary: e.target.value})}
                  rows={3}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold">Categoría *</label>
                <input
                  type="text"
                  required
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm({...uploadForm, category: e.target.value})}
                  placeholder="Ej: Oraciones, Enseñanzas, Guías"
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Se normalizará automáticamente (ej: "oraciones" → "Oraciones")
                </p>
              </div>

              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <input
                    type="checkbox"
                    id="customSlug"
                    checked={customSlug}
                    onChange={(e) => setCustomSlug(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <label htmlFor="customSlug" className="font-semibold">
                    Personalizar slug
                  </label>
                </div>
                
                {customSlug && (
                  <input
                    type="text"
                    value={uploadForm.slug}
                    onChange={(e) => setUploadForm({...uploadForm, slug: e.target.value})}
                    placeholder="slug-personalizado"
                    className={`w-full px-4 py-2 rounded-lg border ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                )}
                
                {slugPreview && (
                  <div className={`mt-2 p-3 rounded-lg ${
                    isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                  }`}>
                    <p className="text-sm">
                      <strong>Vista previa del slug:</strong>{' '}
                      <code className={`${isDarkMode ? 'bg-gray-600' : 'bg-white'} px-2 py-1 rounded`}>
                        {slugPreview}
                      </code>
                    </p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      URL: /documentos/{slugPreview}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block mb-2 font-semibold">Archivo (Markdown o PDF) *</label>
                <input
                  type="file"
                  required
                  accept=".md,.pdf"
                  onChange={(e) => setUploadForm({...uploadForm, file: e.target.files[0]})}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Archivos .md o .pdf (máximo 5MB)
                </p>
              </div>

              <div>
                <label className="block mb-2 font-semibold">URL de Imagen (Opcional)</label>
                <input
                  type="url"
                  value={uploadForm.image_url}
                  onChange={(e) => setUploadForm({...uploadForm, image_url: e.target.value})}
                  placeholder="https://ejemplo.com/imagen.jpg"
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  La imagen se mostrará arriba del documento
                </p>
              </div>

              <div>
                <label className="block mb-2 font-semibold">URL de Video YouTube (Opcional)</label>
                <input
                  type="url"
                  value={uploadForm.video_url}
                  onChange={(e) => setUploadForm({...uploadForm, video_url: e.target.value})}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Botón "Ver Video" se mostrará debajo de la imagen
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="visible"
                  checked={uploadForm.visible}
                  onChange={(e) => setUploadForm({...uploadForm, visible: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="visible" className="font-semibold">
                  Visible públicamente
                </label>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-lg transition-all duration-300"
                >
                  Subir Documento
                </button>
                <button
                  type="button"
                  onClick={() => setUploadFormVisible(false)}
                  className={`flex-1 font-semibold py-3 rounded-lg transition-all duration-300 ${
                    isDarkMode 
                      ? 'bg-gray-700 hover:bg-gray-600' 
                      : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalVisible && selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Editar Metadatos</h2>
              <button
                onClick={() => {
                  setEditModalVisible(false);
                  setSelectedDoc(null);
                }}
                className={`p-2 rounded-lg transition-all duration-300 ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                }`}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block mb-2 font-semibold">Título</label>
                <input
                  type="text"
                  required
                  value={selectedDoc.title}
                  onChange={(e) => setSelectedDoc({...selectedDoc, title: e.target.value})}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold">Resumen</label>
                <textarea
                  value={selectedDoc.summary}
                  onChange={(e) => setSelectedDoc({...selectedDoc, summary: e.target.value})}
                  rows={3}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold">Slug</label>
                <input
                  type="text"
                  required
                  value={selectedDoc.slug}
                  onChange={(e) => setSelectedDoc({...selectedDoc, slug: e.target.value})}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold">Categoría</label>
                <input
                  type="text"
                  required
                  value={selectedDoc.category}
                  onChange={(e) => setSelectedDoc({...selectedDoc, category: e.target.value})}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold">URL de Imagen (Opcional)</label>
                <input
                  type="url"
                  value={selectedDoc.image_url || ''}
                  onChange={(e) => setSelectedDoc({...selectedDoc, image_url: e.target.value})}
                  placeholder="https://ejemplo.com/imagen.jpg"
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold">URL de Video YouTube (Opcional)</label>
                <input
                  type="url"
                  value={selectedDoc.video_url || ''}
                  onChange={(e) => setSelectedDoc({...selectedDoc, video_url: e.target.value})}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="editVisible"
                  checked={selectedDoc.visible}
                  onChange={(e) => setSelectedDoc({...selectedDoc, visible: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="editVisible" className="font-semibold">
                  Visible públicamente
                </label>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-all duration-300"
                >
                  Guardar Cambios
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditModalVisible(false);
                    setSelectedDoc(null);
                  }}
                  className={`flex-1 font-semibold py-3 rounded-lg transition-all duration-300 ${
                    isDarkMode 
                      ? 'bg-gray-700 hover:bg-gray-600' 
                      : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Replace Content Modal */}
      {replaceModalVisible && selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg p-6 max-w-xl w-full ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Reemplazar Contenido</h2>
              <button
                onClick={() => {
                  setReplaceModalVisible(false);
                  setSelectedDoc(null);
                  setReplaceFile(null);
                }}
                className={`p-2 rounded-lg transition-all duration-300 ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                }`}
              >
                <X size={24} />
              </button>
            </div>

            <div className={`mb-4 p-4 rounded-lg ${
              isDarkMode ? 'bg-blue-900/20 text-blue-300' : 'bg-blue-50 text-blue-700'
            }`}>
              <p className="text-sm">
                <strong>Documento:</strong> {selectedDoc.title}
              </p>
              <p className="text-xs mt-1">
                Los metadatos (título, slug, categoría) no cambiarán. Solo se actualizará el contenido.
              </p>
            </div>

            <form onSubmit={handleReplaceSubmit} className="space-y-4">
              <div>
                <label className="block mb-2 font-semibold">Nuevo Archivo (Markdown o PDF)</label>
                <input
                  type="file"
                  required
                  accept=".md,.pdf"
                  onChange={(e) => setReplaceFile(e.target.files[0])}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Archivos .md o .pdf (máximo 5MB)
                </p>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 rounded-lg transition-all duration-300"
                >
                  Reemplazar Contenido
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setReplaceModalVisible(false);
                    setSelectedDoc(null);
                    setReplaceFile(null);
                  }}
                  className={`flex-1 font-semibold py-3 rounded-lg transition-all duration-300 ${
                    isDarkMode 
                      ? 'bg-gray-700 hover:bg-gray-600' 
                      : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDocumentosPage;
